const hasOwn = (source, key) =>
  Object.prototype.hasOwnProperty.call(source || {}, key);

const toNumericId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeTemplateIds = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const uniqueValues = new Set();

  values.forEach((value) => {
    const normalized = toNumericId(value);
    if (normalized !== null) {
      uniqueValues.add(normalized);
    }
  });

  return Array.from(uniqueValues).sort((a, b) => a - b);
};

export const buildTemplateFiltersSnapshot = ({
  selectedSeason = [],
  selectedCompetition = [],
  selectedMatchCount = null,
  selectedMatches = [],
  selectedParameters = { checkedIds: [] },
} = {}) => {
  const normalizedMatchCountId = toNumericId(selectedMatchCount?.id);

  return {
    seasonIds: normalizeTemplateIds(selectedSeason.map((item) => item?.id)),
    competitionIds: normalizeTemplateIds(
      selectedCompetition.map((item) => item?.competition_id)
    ),
    matchCountId: normalizedMatchCountId,
    matchIds: normalizeTemplateIds(selectedMatches.map((item) => item?.id)),
    parameterIds: normalizeTemplateIds(selectedParameters?.checkedIds || []),
  };
};

export const buildTemplateFiltersSignature = (filters = {}) =>
  JSON.stringify({
    seasonIds: normalizeTemplateIds(filters?.seasonIds),
    competitionIds: normalizeTemplateIds(filters?.competitionIds),
    matchCountId: toNumericId(filters?.matchCountId),
    matchIds: normalizeTemplateIds(filters?.matchIds),
    parameterIds: normalizeTemplateIds(filters?.parameterIds),
  });

const buildSelectedParametersFromGroups = (parameterGroups, parameterIds) => {
  if (!Array.isArray(parameterGroups) || parameterGroups.length === 0) {
    return null;
  }

  const parameterIdSet = new Set(parameterIds);
  const checkedParams = parameterGroups.flatMap((group) => {
    const params = Array.isArray(group?.[1]) ? group[1] : [];
    return params.filter((item) => parameterIdSet.has(toNumericId(item?.id)));
  });

  if (checkedParams.length === 0) {
    return null;
  }

  return {
    checkedIds: checkedParams.map((item) => item.id),
    params: checkedParams,
  };
};

export const resolveTemplateHydrationSelection = ({
  templateFilters = {},
  availableSeasons = [],
  fallbackSeasonSelection = [],
  competitions = [],
  matches = [],
  matchesCountList = [],
  parameterGroups = [],
} = {}) => {
  const hasSeasonIds = hasOwn(templateFilters, "seasonIds");
  const hasCompetitionIds = hasOwn(templateFilters, "competitionIds");
  const hasMatchCountId = hasOwn(templateFilters, "matchCountId");
  const hasMatchIds = hasOwn(templateFilters, "matchIds");
  const hasParameterIds = hasOwn(templateFilters, "parameterIds");

  let selectedSeason = null;
  if (hasSeasonIds) {
    const seasonIds = normalizeTemplateIds(templateFilters?.seasonIds);
    if (seasonIds.length === 0) {
      selectedSeason = [];
    } else {
      const selectedSeasonSet = new Set(seasonIds);
      const matchedSeasons = availableSeasons.filter((item) =>
        selectedSeasonSet.has(toNumericId(item?.id))
      );
      selectedSeason =
        matchedSeasons.length > 0 ? matchedSeasons : fallbackSeasonSelection;
    }
  }

  let selectedCompetition = null;
  if (hasCompetitionIds) {
    const competitionIds = normalizeTemplateIds(templateFilters?.competitionIds);
    if (competitionIds.length === 0) {
      selectedCompetition = [];
    } else {
      const selectedCompetitionSet = new Set(competitionIds);
      const matchedCompetitions = competitions.filter((item) =>
        selectedCompetitionSet.has(toNumericId(item?.competition_id))
      );
      selectedCompetition =
        matchedCompetitions.length > 0 ? matchedCompetitions : competitions;
    }
  }

  let selectedMatchCount = null;
  if (hasMatchCountId) {
    const requestedMatchCountId = toNumericId(templateFilters?.matchCountId);
    if (requestedMatchCountId !== null) {
      selectedMatchCount =
        matchesCountList.find(
          (item) => toNumericId(item?.id) === requestedMatchCountId
        ) ||
        matchesCountList.find((item) => toNumericId(item?.id) === 1) ||
        null;
    }
  }

  let selectedMatches = null;
  if (hasMatchIds) {
    const matchIds = normalizeTemplateIds(templateFilters?.matchIds);
    if (matchIds.length === 0) {
      selectedMatches = [];
    } else {
      const selectedMatchesSet = new Set(matchIds);
      const matchedMatches = matches.filter((item) =>
        selectedMatchesSet.has(toNumericId(item?.id))
      );
      selectedMatches = matchedMatches.length > 0 ? matchedMatches : null;
    }
  }

  let selectedParameters = null;
  if (hasParameterIds) {
    const parameterIds = normalizeTemplateIds(templateFilters?.parameterIds);
    if (parameterIds.length === 0) {
      selectedParameters = { checkedIds: [], params: [] };
    } else {
      selectedParameters = buildSelectedParametersFromGroups(
        parameterGroups,
        parameterIds
      );
    }
  }

  return {
    selectedSeason,
    selectedCompetition,
    shouldSetMatchCount: hasMatchCountId,
    selectedMatchCount,
    selectedMatches,
    selectedParameters,
  };
};
